import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import { useReactToPrint } from "react-to-print";
import { CheckCircleIcon, NoSymbolIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PrinterIcon } from "@heroicons/react/24/solid";
import { Column } from "primereact/column";
import {
	DataTable,
	DataTableExpandedRows,
	DataTableRowEvent,
	DataTableValueArray,
} from "primereact/datatable";
import { SharedAlert } from "../../app/siteSlice";
import { AlertLevel } from "../../components/AlertMessage";
import ButtonWithConfirm from "../../components/ButtonWithConfirm";
import FormattedDate from "../../components/FormattedDate";
import TextWithTip from "../../components/TextWithTip";
import PrintPeerReviewDocument from "../peerReviews/PrintPeerReviewDocument";
import { StimulusMaterial } from "../stimulusMaterial/StimulusMaterial";
import { PromptSteps } from "./AddEditPrompt";
import PromptDescription from "./PromptDescription";
import {
	PromptListViewModel,
	PromptType,
	useDeleteCoursePromptMutation,
	useDisablePromptMutation,
	useEnablePromptMutation,
} from "./promptsApiSlice";
import "./td_ProseMirror.css";

interface PromptListTableProps {
	prompts: PromptListViewModel[];
	handlePageChange: (page: number) => void;
	selectedPromptId?: number;
	setSelectedPromptId: (id?: number) => void;
	setAlert: (alert: SharedAlert) => void;
}

interface PromptListTableData {
	promptId: number;
	title: string;
	row: PromptListViewModel;
}

const mapPromptData = (data: PromptListViewModel[]): PromptListTableData[] => {
	return data.map((row) => {
		return {
			promptId: row.promptId,
			title: row.title,
			row: row,
		};
	});
};

export default function PromptListTable(props: PromptListTableProps) {
	const navigate = useNavigate();

	const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray>();

	const data = mapPromptData(props.prompts);

	useEffect(() => {
		if (!props.selectedPromptId) {
			setExpandedRows({});
		} else {
			expandRow();
		}
	}, [props.selectedPromptId]);

	useEffect(() => {
		if (props.selectedPromptId) {
			expandRow();
		}
	}, [props.prompts]);

	function expandRow() {
		const promptId =
			props.prompts.find((p) => p.promptId == props.selectedPromptId)?.promptId ?? 0;
		setExpandedRows({ [promptId]: true });
	}

	const onRowExpand = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);

			const tr = target.closest("tr");
			tr && tr?.classList.add("expanded");
		}
		setExpandedRows({ [event.data.promptId]: true });
	};

	const onRowCollapse = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);
		}
		setExpandedRows({});
		props.setSelectedPromptId(undefined);
	};

	const rowExpansionTemplate = (data: PromptListTableData) => {
		const prompt = props.prompts.find((p) => p.promptId === data.promptId);
		return (
			<PromptExpandedTemplate
				prompt={prompt}
				handlePageChange={props.handlePageChange}
				setAlert={props.setAlert}></PromptExpandedTemplate>
		);
	};

	const onRowClick = (event: DataTableRowEvent) => {
		const xRows: any = expandedRows;

		if (xRows && xRows[event.data.promptId]) {
			onRowCollapse(event);
		} else {
			onRowExpand(event);
		}
	};

	const enabledBodyTemplate = (data: PromptListTableData) => {
		const tooltipContent = data.row.enabled
			? "Enabled on " + new Date(data.row.statusDate).toLocaleDateString()
			: "Disabled";

		return (
			<span className="flex justify-center">
				{data.row.enabled ? (
					<CheckCircleIcon
						className="size-6 text-green-500"
						data-tooltip-id={`enabled-${data.promptId}-tooltip`}
					/>
				) : (
					<NoSymbolIcon
						className="size-6 text-red-500"
						data-tooltip-id={`enabled-${data.promptId}-tooltip`}
					/>
				)}
				<Tooltip
					id={`enabled-${data.promptId}-tooltip`}
					place="top-start"
					content={tooltipContent}
				/>
			</span>
		);
	};

	const promptTypeBodyTemplate = (data: PromptListTableData) => {
		const bgColor =
			data.row.promptType === PromptType.System
				? "bg-blue-400"
				: data.row.promptType === PromptType.District
					? "bg-green-300"
					: data.row.promptType === PromptType.School
						? "bg-pink-400"
						: "bg-orange-400";

		return (
			<div className="flex justify-center">
				<span
					className={`${bgColor} flex size-5 shrink-0 items-center justify-center rounded-full`}
					data-tooltip-id={`promptType-${data.promptId}-tooltip`}>
					{data.row.promptType === PromptType.System ? (
						<span aria-hidden="true" className="text-sm text-white">
							P
						</span>
					) : data.row.promptType === PromptType.District ? (
						<span aria-hidden="true" className="text-sm text-white">
							D
						</span>
					) : data.row.promptType === PromptType.School ? (
						<span aria-hidden="true" className="text-sm text-white">
							S
						</span>
					) : (
						<span aria-hidden="true" className="text-sm text-white">
							T
						</span>
					)}
					<Tooltip
						id={`promptType-${data.promptId}-tooltip`}
						place="top-start"
						content={`${data.row.promptType} Prompt`}
					/>
				</span>
			</div>
		);
	};

	const genreContentAreaBodyTemplate = (data: PromptListTableData) => {
		return (
			<span className="whitespace-nowrap">
				<span>{data.row.genreName}</span>
				<div className="mt-1 text-gray-500 text-sm">
					<TextWithTip
						uniqueName={`content-${data.promptId}`}
						text={data.row.contentAreas.join(", ")}
					/>
				</div>
			</span>
		);
	};

	const titleBodyTemplate = (data: PromptListTableData) => {
		return <div className="line-clamp-2 hover:line-clamp-none">{data.row.title}</div>;
	};

	const descriptionBodyTemplate = (data: PromptListTableData) => {
		return (
			<div
				className="ProseMirror max-w-[750px] max-h-12 line-clamp-2 hover:line-clamp-none hover:max-h-none"
				dangerouslySetInnerHTML={{
					__html: data.row.description ?? "",
				}}></div>
		);
	};

	const editBodyTemplate = (data: PromptListTableData) => {
		return (
			<button
				id={`edit-${data.promptId}-button`}
				className="mr-2"
				onClick={() => navigate(`/writing/prompt?promptId=${data.promptId}`)}>
				<PencilIcon className="size-5" />
			</button>
		);
	};

	return (
		<DataTable
			value={data}
			dataKey="promptId"
			stripedRows
			expandedRows={expandedRows}
			onRowClick={onRowClick}
			onRowExpand={onRowExpand}
			onRowCollapse={onRowCollapse}
			rowExpansionTemplate={rowExpansionTemplate}
			selectionMode="single"
			emptyMessage="No results found"
			className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
			style={{ paddingTop: "16px" }}>
			<Column expander={true}></Column>
			<Column
				header="Enabled"
				body={enabledBodyTemplate}
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="Type"
				body={promptTypeBodyTemplate}
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column header="Title" body={titleBodyTemplate}></Column>
			<Column
				header="Genre / Content Area(s)"
				body={genreContentAreaBodyTemplate}
				className="max-w-72"></Column>
			<Column header="Description" body={descriptionBodyTemplate} className="!pr-3"></Column>
			<Column body={editBodyTemplate} className="!pl-1 !pr-2"></Column>
		</DataTable>
	);
}

interface PromptExpandedProps {
	prompt?: PromptListViewModel;
	handlePageChange: (page: number) => void;
	setAlert: (alert: SharedAlert) => void;
}

function PromptExpandedTemplate({ prompt, handlePageChange, setAlert }: PromptExpandedProps) {
	const advancedSettings = prompt?.coursePromptAdvancedSettings;
	const reviewSettings = prompt?.coursePromptPeerReviewSettings;

	const contentRef = useRef<HTMLDivElement>(null);
	const printPeerReview = useReactToPrint({ contentRef });

	const [enablePrompt] = useEnablePromptMutation();
	const [disablePrompt] = useDisablePromptMutation();
	const [deleteCoursePrompt] = useDeleteCoursePromptMutation();

	const navigate = useNavigate();

	function handlePrintPeerReview() {
		printPeerReview();
	}

	function handleDisable(id: number) {
		disablePrompt([id])
			.unwrap()
			.then(() => {
				handlePageChange(0);
			});
	}

	function handleEnable(id: number) {
		enablePrompt([id])
			.unwrap()
			.then(() => {
				handlePageChange(0);
			});
	}

	function handleDeleteCoursePrompt(id: number, name: string) {
		deleteCoursePrompt(id)
			.unwrap()
			.then(() => {
				setAlert({
					level: AlertLevel.Info,
					message: `The prompt "${name}" has been deleted from this course. To reactivate the prompt, use the Reactivate Prompt button below.`,
				});
				handlePageChange(0);
			});
	}

	function handleNavigate(step: PromptSteps, section?: string) {
		navigate(`/writing/prompt?promptId=${prompt?.promptId}`, {
			state: {
				step: step,
				section: section,
			},
		});
	}

	return (
		prompt && (
			<div className="-mx-4 -my-3 p-4 bg-gray-100 lg:flex lg:justify-between lg:gap-x-4">
				<div
					className={`${advancedSettings && reviewSettings ? "lg:w-2/5 w-full" : "w-full"} bg-white p-4 h-full`}>
					<div className="flex items-start justify-between gap-4">
						<div>
							<h2 className="leading-6 text-gray-900">{prompt.title}</h2>
						</div>
						{prompt.canEdit && (
							<button
								id={`edit-prompt-${prompt.promptId}-button`}
								className="border-2 p-1 rounded-md shadow-sm border-gray-900/20">
								<PencilIcon
									className="size-6"
									onClick={() => handleNavigate(PromptSteps.Prompt)}
								/>
							</button>
						)}
					</div>
					<PromptDescription className="mt-4" html={prompt.description} />
					<StimulusMaterial className="mt-4" stimuli={prompt.stimulusMaterial} />
					{prompt.enabled ? (
						<button
							className="btn bg-red-600 text-white mt-4"
							onClick={() => handleDisable(prompt.promptId)}>
							<span className="flex items-center">
								<NoSymbolIcon className="size-5 mr-2" /> Disable
							</span>
						</button>
					) : (
						<button
							className="btn bg-green-500 text-white mt-4"
							onClick={() => handleEnable(prompt.promptId)}>
							<span className="flex items-center">
								<CheckCircleIcon className="size-5 mr-2" /> Enable
							</span>
						</button>
					)}
				</div>
				{advancedSettings && reviewSettings && (
					<div className="bg-white p-4 h-full w-full mt-4 lg:w-3/5 lg:mt-0">
						<div className="flex justify-between items-center mb-2">
							<h2 className="leading-6 text-gray-900 ">
								Course Prompt Settings Summary
							</h2>
							{prompt.canEdit && !prompt.isShared && (
								<ButtonWithConfirm
									id={`delete-prompt-${prompt.promptId}-button`}
									className="border-2 p-1 rounded-md shadow-sm border-red-700/20"
									message="Are you sure you want to delete this prompt from your course?"
									confirmLabel="Yes"
									cancelLabel="No"
									onConfirm={() =>
										handleDeleteCoursePrompt(prompt.promptId, prompt.title)
									}>
									<TrashIcon className="size-5 text-red-600" />
								</ButtonWithConfirm>
							)}
						</div>
						<div className="flex gap-x-7">
							<div className="w-1/2">
								<div>
									<CoursePromptSectionHeader
										title="Assignment"
										onEdit={() => handleNavigate(PromptSteps.Assignment)}
									/>
									<div className="divide-y divide-slate-500/30">
										<div className="flex justify-between items-center">
											<p>Students Assigned</p>
											<p
												className="font-medium"
												data-tooltip-id={`assignedTo-${prompt.promptId}`}>
												{advancedSettings.assignedToNames.length}
											</p>
											{advancedSettings.assignedToNames.length > 0 && (
												<Tooltip
													className="z-10"
													id={`assignedTo-${prompt.promptId}`}
													place="right">
													<div>
														{advancedSettings.assignedToNames.map(
															(s) => {
																return <p key={s}>{s}</p>;
															}
														)}
													</div>
												</Tooltip>
											)}
										</div>
										<div className="flex justify-between items-center">
											<p>
												{advancedSettings.dueDateEnabled
													? "Due Date"
													: "Due Date Enabled"}
											</p>
											<p className="font-medium">
												{advancedSettings.dueDateEnabled ? (
													<FormattedDate
														date={advancedSettings.dueDate}
													/>
												) : (
													"NO"
												)}
											</p>
										</div>
										{advancedSettings.dueDateEnabled && (
											<div className="divide-y divide-slate-500/30">
												<div className="flex justify-between items-center">
													<p>Due Date Enforced</p>
													<p className="font-medium">
														{advancedSettings.dueDateRequired
															? "YES"
															: "NO"}
													</p>
												</div>
												<div className="flex justify-between items-center">
													<p>Due Date Reminders</p>
													<p
														className="font-medium"
														data-tooltip-id={`dueDateReminders-${prompt.promptId}`}>
														{advancedSettings.selectedReminders.length}
													</p>
													{advancedSettings.selectedReminders.length >
														0 && (
														<Tooltip
															className="z-10"
															id={`dueDateReminders-${prompt.promptId}`}
															place="right">
															<div>
																{advancedSettings.selectedReminders.map(
																	(r) => {
																		if (r === 24) {
																			return (
																				<p key={r}>
																					1 day before
																				</p>
																			);
																		} else if (r === 72) {
																			return (
																				<p key={r}>
																					3 days before
																				</p>
																			);
																		} else if (r === 168) {
																			return (
																				<p key={r}>
																					1 week before
																				</p>
																			);
																		}
																	}
																)}
															</div>
														</Tooltip>
													)}
												</div>
											</div>
										)}
									</div>
								</div>
								<div className="mt-4">
									<CoursePromptSectionHeader
										title="Organizer"
										onEdit={() => handleNavigate(PromptSteps.Organizer)}
									/>
									<div className="flex justify-between items-center">
										<p>
											{advancedSettings.isOrganizerRequired
												? "Assigned Organizer"
												: "Organizer Assigned"}
										</p>
										<p className="font-medium text-right">
											{advancedSettings.isOrganizerRequired
												? (advancedSettings.selectedOrganizerName ??
													"Student Choice")
												: "NO"}
										</p>
									</div>
								</div>
								<div className="mt-4">
									<CoursePromptSectionHeader
										title="Peer Review"
										showPeerReviewPrint={reviewSettings.allowReviews}
										onEdit={() => handleNavigate(PromptSteps.PeerReviews)}
										onPrintPeerReview={handlePrintPeerReview}
									/>
									<div ref={contentRef} className="hidden print:block">
										<PrintPeerReviewDocument
											title={prompt.title}
											generalQuestions={
												reviewSettings?.generalReviewQuestions ?? []
											}
											reviewQuestions={reviewSettings?.reviewQuestions ?? []}
											customQuestions={
												reviewSettings?.customReviewQuestions ?? []
											}
										/>
									</div>
									{!reviewSettings.allowReviews ? (
										<div className="flex justify-between items-center">
											<p>Peer Reviews Enabled</p>
											<p className="font-medium">
												{reviewSettings.allowReviews ? "YES" : "NO"}
											</p>
										</div>
									) : (
										<div className="divide-y divide-slate-500/30">
											<div className="flex justify-between items-center">
												<p>Anonymous Reviewer</p>
												<p className="font-medium">
													{reviewSettings.anonymousReviewers
														? "YES"
														: "NO"}
												</p>
											</div>
											<div className="flex justify-between items-center">
												<p>Anonymous Author</p>
												<p className="font-medium">
													{reviewSettings.anonymousAuthor ? "YES" : "NO"}
												</p>
											</div>
											<div className="flex justify-between items-center">
												<p>Sentence Starters Enabled</p>
												<p className="font-medium">
													{reviewSettings.allowSentenceStarters
														? "YES"
														: "NO"}
												</p>
											</div>
											<div className="flex justify-between items-center">
												<p>Total Questions Chosen</p>
												<p
													className="font-medium"
													data-tooltip-id={`reviewQuestions-${prompt.promptId}`}>
													{reviewSettings.generalReviewQuestions.length +
														reviewSettings.customReviewQuestions
															.length +
														reviewSettings.reviewQuestions.length}
												</p>
												<Tooltip
													className="z-10"
													id={`reviewQuestions-${prompt.promptId}`}
													place="bottom">
													<div>
														<div className="flex flex-col">
															<strong>Required</strong>
															{reviewSettings.generalReviewQuestions.map(
																(q) => {
																	return (
																		<p key={q.id}>
																			{q.question}
																		</p>
																	);
																}
															)}
														</div>
														<div className="flex flex-col">
															<strong>Genre Specific</strong>
															{reviewSettings.reviewQuestions.length >
															0
																? reviewSettings.reviewQuestions.map(
																		(q) => {
																			return (
																				<p key={q.id}>
																					{q.question}
																				</p>
																			);
																		}
																	)
																: "None Selected"}
														</div>
														<div className="flex flex-col">
															<strong>Custom</strong>
															{reviewSettings.customReviewQuestions
																.length > 0
																? reviewSettings.customReviewQuestions.map(
																		(q, index) => {
																			return (
																				<p
																					key={`custom-${index}`}>
																					{q.question}
																				</p>
																			);
																		}
																	)
																: "None Selected"}
														</div>
													</div>
												</Tooltip>
											</div>
										</div>
									)}
								</div>
							</div>
							<div className="w-1/2">
								<div>
									<CoursePromptSectionHeader
										title="Options"
										onEdit={() => handleNavigate(PromptSteps.Options)}
									/>
									<div className="divide-y divide-slate-500/30">
										<div className="flex justify-between items-center">
											<p>Allow Copy & Paste</p>
											<p className="font-medium">
												{advancedSettings.allowCopyPaste ? "YES" : "NO"}
											</p>
										</div>
										<div className="flex justify-between items-center">
											<p>
												{advancedSettings.timerEnabled
													? "Time Allowed"
													: "Timer Enabled"}
											</p>
											<p className="font-medium">
												{advancedSettings.timerEnabled
													? advancedSettings.timeAllowed + " min"
													: "NO"}
											</p>
										</div>
										<div className="flex justify-between items-center">
											<p>
												{advancedSettings.retriesEnabled
													? "Resubmissions Allowed"
													: "Resubmissions Enabled"}
											</p>
											<p className="font-medium text-right">
												{advancedSettings.retriesEnabled
													? advancedSettings.retriesAllowed + " drafts"
													: "NO"}
											</p>
										</div>
										<div className="flex justify-between items-center">
											<p>
												{advancedSettings.finalizeEnabled
													? "Finalize Allowed After"
													: "Finalize Enabled"}
											</p>
											<p className="font-medium">
												{advancedSettings.finalizeEnabled
													? advancedSettings.finalizeDraftNumber +
														" drafts"
													: "NO"}
											</p>
										</div>
										<div className="flex justify-between items-center">
											<p>Title Required</p>
											<p className="font-medium">
												{advancedSettings.titleRequired ? "YES" : "NO"}
											</p>
										</div>
										<div className="flex justify-between items-center">
											<p>Require Cited Sources</p>
											<p className="font-medium">
												{advancedSettings.requireSources ? "YES" : "NO"}
											</p>
										</div>
										<div className="flex justify-between items-center">
											<p>Hide Feedback Until</p>
											<p className="font-medium w-20 text-right">
												{advancedSettings.grammarCheckDraftThreshold} drafts
											</p>
										</div>
										<div className="flex justify-between items-center">
											<p>Hide Score Until</p>
											<p className="font-medium">
												{advancedSettings.scoreDraftNumber} drafts
											</p>
										</div>
										<div
											className={`${advancedSettings.specializedVocabularyList && advancedSettings.specializedVocabularyList?.length > 0 ? "" : "flex justify-between items-center"}`}>
											<p>Specialized Vocabulary</p>
											<p className="font-medium ml-4">
												{advancedSettings.specializedVocabularyList
													?.length > 0 ? (
													<span className="text-sm">
														{advancedSettings.specializedVocabularyList.join(
															", "
														)}
													</span>
												) : (
													"NO"
												)}
											</p>
										</div>
									</div>
								</div>
								{advancedSettings.canSetPlagiarismSettings && (
									<div className="mt-4">
										<CoursePromptSectionHeader
											title="Plagiarism Checker"
											onEdit={() =>
												handleNavigate(
													PromptSteps.Options,
													"PlagiarismSettings"
												)
											}
										/>
										{!advancedSettings.plagiarismCheckerEnabled ? (
											<div className="flex justify-between items-center">
												<p>Plagiarism Checker Enabled</p>
												<p className="font-medium">
													{advancedSettings.plagiarismCheckerEnabled
														? "YES"
														: "NO"}
												</p>
											</div>
										) : (
											<div
												className={`${advancedSettings.plagiarismCheckerAllDrafts ? "flex justify-between items-center" : ""}`}>
												<p>Plagiarism Checked Drafts</p>
												<p className="font-medium ml-4">
													{advancedSettings.plagiarismCheckerAllDrafts ? (
														"ALL"
													) : (
														<span className="text-sm">
															{
																advancedSettings.plagiarismCheckedDrafts
															}
														</span>
													)}
												</p>
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		)
	);
}

interface CoursePromptSectionHeaderProps {
	title: string;
	showPeerReviewPrint?: boolean;
	onEdit: () => void;
	onPrintPeerReview?: () => void;
}

function CoursePromptSectionHeader(props: CoursePromptSectionHeaderProps) {
	return (
		<div className="flex justify-between items-center">
			<h3 className="font-medium">{props.title}</h3>
			<div className="flex flex-row gap-x-2">
				{props.showPeerReviewPrint && (
					<PrinterIcon
						className="size-5 text-gray-500 hover:cursor-pointer hover:text-gray-700"
						onClick={props.onPrintPeerReview}
					/>
				)}
				<PencilIcon
					className="size-5 text-gray-500 hover:cursor-pointer hover:text-gray-700"
					onClick={props.onEdit}
				/>
			</div>
		</div>
	);
}
